











































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import MobileDetect from 'mobile-detect';
import { FormState } from '@/components/prine/enums/FormState';
import ConfirmModal from '@/components/prine/ConfirmModal.vue';

import type { Project } from '@/models/project';
import type { User } from '@/models/user';
import type { PMLesson } from '@/models/pmlesson';
import type { PMPhase } from '@/models/pmphase';
import type { PMSubPhase } from '@/models/pmsubphase';
import type { Tag } from '@/models/tag';
import { ApprovalState } from '@/components/prine/enums/ApprovalState';

import { pmSubPhaseWebservice } from '@/webservices/pmsubphase';
import { pmLessonWebservice } from '@/webservices/pmlesson';
import { tagWebservice } from '@/webservices/tag';
import type { CompanySetting } from '@/models/companySetting';
import WysiwygEditor from '@/components/prine/WysiwygEditor.vue';
import { projectWebservice } from '@/webservices/project';
import PrineButton from '@/components/prine/PrineButton.vue';
import { pmPhaseWebservice } from '@/webservices/pmphase';
import PreviewLessons from '@/components/knowledge/lessons/PreviewLessons.vue';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'StoreUpdatePMLessonModal',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        ConfirmModal,
        WysiwygEditor,
        PrineButton,
        PreviewLessons,
    },
})
export default class StoreUpdatePMLessonModal extends Vue {
    get lessonsDisabled() {
        return this.subPhase === null;
    }

    private get isDesktop() {
        const isNotDesktop = window.matchMedia('only screen and (max-width: 1300px)').matches;
        const md = new MobileDetect(window.navigator.userAgent);
        return !(md.mobile() || md.phone() || md.tablet() || isNotDesktop);
    }

    public FormState = FormState;

    // Data
    private disabled: boolean = false;
    private errorMsg: string = '';
    private isConfirmModalActive: boolean = false;
    private showMandatoryOption: boolean = false;
    private state: FormState = FormState.New;

    private mandatory: boolean = false;

    private lesson: PMLesson = {} as PMLesson;
    private tags: Tag[] = new Array<Tag>();
    private selectedTags: Tag[] = new Array<Tag>();
    private projects: Project[] = new Array<Project>();
    private parentLesson: PMLesson | null = null;
    private lessons = new Array<PMLesson>();
    private checkedParentLesson: PMLesson[] = new Array<PMLesson>();

    private loading = {
        data: false,
        color: '#fdc05d',
        size: '40px',
    };

    private filteredSubPhases = new Array<PMSubPhase>();
    private phases = new Array<PMPhase>();
    private phase: PMPhase = {} as PMPhase;
    private subPhases = new Array<PMSubPhase>();
    private subPhase: PMSubPhase | null = null;
    private selectedProjectDropdown: Project = {} as Project;

    @Getter('account/userInfo') private userInfo: User;
    @Getter('project/selectedProject') private selectedProject: Project;
    @Getter('account/companySetting') private companySetting: CompanySetting;
    @Getter('utils/currentLocale') private currentLocale: string;

    @Prop() private selectedPhase: PMPhase;
    @Prop() private selectedLesson: PMLesson;

    @Watch('phase')
    private pmPhaseChanged(newPhase, oldPhase) {
        this.loadSubPhases();

        if (oldPhase === newPhase || (this.subPhase && this.subPhase.phase.id === newPhase.id)) {
            return;
        }
        this.subPhase = null;
        this.parentLesson = null;
    }

    @Watch('subPhase')
    private onSubPhaseChanged(newPhase, oldPhase) {
        if (oldPhase === newPhase || !newPhase) {
            return;
        }

        this.loadLessons(newPhase.id as number);
        this.parentLesson = null;
    }

    private selectedParentLesson(value: PMLesson[]) {
        this.checkedParentLesson = value;
        if (value.length !== 0) {
            this.parentLesson = value[0];
        } else {
            this.parentLesson = null;
        }
    }

    // Mounted is called initially
    private mounted() {
        this.loading.data = true;

        const promises = [this.loadTags(), this.loadPhases(), this.loadProjects()];

        if (this.selectedLesson && this.selectedLesson.id) {
            promises.push(this.loadLesson(this.selectedLesson.id));
            this.state = FormState.Update;
        }
        // If the user is content admin or master show mandatory option
        this.showMandatoryOption = hasPermission(PERMISSIONS.APPEX_PM_LESSON_MANDATORY);

        Promise.all(promises).then(() => {
            this.loading.data = false;
        });
    }

    private async loadLessons(sub_phase_id: number): Promise<void> {
        try {
            const response = await pmLessonWebservice.load(sub_phase_id);
            this.lessons = response.data;

            if (this.lesson.parent && this.lesson.parent.pm_sub_phase_id === sub_phase_id) {
                this.parentLesson = this.lesson.parent;
                this.checkedParentLesson = [];
                this.checkedParentLesson.push(this.parentLesson);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadPhases(): Promise<void> {
        try {
            const response = await pmPhaseWebservice.load();
            this.phases = response.data;

            if (this.selectedPhase) {
                this.phase = this.selectedPhase;
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadSubPhases(): Promise<void> {
        try {
            const response = await pmSubPhaseWebservice.load(this.phase.id, ApprovalState.ALL);
            this.subPhases = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadProjects(): Promise<void> {
        try {
            const res = await projectWebservice.loadAll();
            this.projects = res.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadTags() {
        try {
            const response = await tagWebservice.load();
            this.tags = response.data;

            if (!this.selectedLesson) {
                this.selectedTags = this.tags.filter((tag) => tag.default);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadLesson(lessonId: number): Promise<void> {
        try {
            const response = await pmLessonWebservice.getById(lessonId);
            this.lesson = response.data;
            this.selectedTags = response.data.tags;

            if (this.lesson.project) {
                this.selectedProjectDropdown = this.lesson.project;
            }
            if (this.lesson.sub_phase) {
                this.subPhase = this.lesson.sub_phase;
                this.phase = this.subPhase.phase;
            }
        } catch (error) {
            console.error(error);
        }
    }

    private save() {
        this.disabled = true;
        if (!this.validate()) {
            this.disabled = false;
            return 0;
        }
        if (this.subPhase !== null) {
            if (this.state === FormState.New) {
                // tslint:disable-next-line:max-line-length
                pmLessonWebservice
                    .store(
                        this.lesson,
                        this.subPhase.id,
                        this.lesson.mandatory,
                        this.selectedTags,
                        null,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then(() => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.store.lesson').toString(),
                            type: 'is-success',
                        });
                        this.toggleModal(this.subPhase);
                        this.$emit('syncKnowledge');
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            } else if (this.state === FormState.Update) {
                // tslint:disable-next-line:max-line-length
                pmLessonWebservice
                    .update(
                        this.lesson,
                        this.subPhase,
                        this.lesson.mandatory,
                        this.selectedTags,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then(() => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.update.lesson').toString(),
                            type: 'is-success',
                        });
                        this.toggleModal(this.subPhase);
                        this.$emit('syncKnowledge');
                        this.disabled = false;
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            }
        }
    }

    private toggleConfirmModal(answer = false) {
        this.isConfirmModalActive = !this.isConfirmModalActive;
        if (answer === true) {
            this.archive();
        }
    }

    private toggleModal(subPhase: PMSubPhase | null = null) {
        this.$emit('toggleStoreUpdateLessonModal', this.selectedPhase, subPhase);
    }

    private archive() {
        pmLessonWebservice
            .remove(this.lesson)
            .then(() => {
                this.$buefy.toast.open({
                    duration: 3000,
                    message: this.$t('toastMessages.delete.lesson').toString(),
                    type: 'is-success',
                });
                this.toggleModal(this.subPhase);
                this.$emit('syncKnowledge');
            })
            .catch((error) => {
                console.log(error);
            });
    }

    private validate() {
        if (!this.lesson.insight || this.lesson.insight === '') {
            this.errorMsg = 'insight';
            return 0;
        } else if (this.subPhase === null || !this.subPhase.title || this.subPhase.title === '') {
            this.errorMsg = 'subphase';
            return 0;
        } else {
            return true;
        }
    }
}
